import { Box, Stack, Typography, useTheme } from '@mui/joy';
import { ArrowBendDownRight, ArrowRight } from '@phosphor-icons/react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from 'core/components';
import { Loader, TextEllipsis, TextHighlighter } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { GetManyResponse } from 'core/types';
import { Direction, Level } from 'theme/enums';

import { getArrowIconStyle, getTaxonomyStyle, OptionWrapper, taxonomyPathStyle } from './style';
import { TreeChip } from './TreeChip';
import { ITreeFormFieldOptions, ITreeItem, ITreePathItem } from './types';
import { buildBreadcrumbLabels, createTreeItemFromPath, filterPathItems } from './utils';

interface ITreeFormFieldProps<T extends ITreeItem> {
  name: string;
  label?: string;
  allItemsLabel?: string;
  placeholder?: string;
  getOptions: (requestPayload: ITreeFormFieldOptions) => Promise<GetManyResponse<T>>;
  isLoading?: boolean;
  selectedItem?: T | null;
  childItems?: T[];
  onChange?: (item: T | null) => void;
  required?: boolean;
}

export const TreeFormField = <T extends ITreeItem>({
  name,
  label,
  placeholder,
  getOptions,
  isLoading = false,
  selectedItem,
  childItems = [],
  onChange,
  allItemsLabel,
  required = false,
}: ITreeFormFieldProps<T>) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { control, errors, setValue } = useFormContext();

  const fieldError = errors?.[name];

  const handleItemChange = (item: T | null) => {
    setValue(name, item);
    onChange?.(item);
  };

  const renderOption = (option: T, getLabel: (option: T) => string, inputValue: string) => {
    if (!option.path) {
      return (
        <Typography level={Level.BODY_XS}>
          <TextHighlighter
            text={getLabel(option)}
            highlight={inputValue}
            sx={{ color: theme.palette.neutral[800] }}
          />
        </Typography>
      );
    }

    const filteredPath = filterPathItems(option.path);
    const { pathLabels, itemName } = buildBreadcrumbLabels(filteredPath);

    return (
      <OptionWrapper>
        {pathLabels.map((name: string, index: number) => (
          <Fragment key={`${name}-${index}`}>
            <TextEllipsis
              level={Level.BODY_XS}
              content={name}
              lineHeight={14}
              style={taxonomyPathStyle}
            />
            <ArrowRight style={getArrowIconStyle(theme)} />
          </Fragment>
        ))}
        <Typography level={Level.BODY_XS}>
          <TextHighlighter text={itemName} highlight={inputValue} sx={getTaxonomyStyle(theme)} />
        </Typography>
      </OptionWrapper>
    );
  };

  const renderPathItem = (pathItem: ITreePathItem, isMain: boolean, onClick: () => void) => {
    const pathLevel = pathItem.level;

    return (
      <Stack
        key={pathItem.id}
        direction={Direction.ROW}
        sx={{
          paddingLeft: pathLevel > 0 ? `calc(${pathLevel} * 8px)` : 0,
          alignItems: 'center',
        }}
      >
        {pathLevel > 0 && <ArrowBendDownRight />}
        <TreeChip
          label={pathItem.name || ''}
          onClick={onClick}
          isMainSelected={isMain}
          isSelected
        />
      </Stack>
    );
  };

  return (
    <Stack spacing={2}>
      <FormField
        key={selectedItem?.id}
        name={name}
        type={InputType.AUTOCOMPLETE}
        inputProps={{
          label,
          placeholder,
          required,
          control,
          error: fieldError,
          getOptions,
          getKey: (item: T) => item.id,
          getLabel: (item: T) => item.name,
          renderOptions: renderOption,
        }}
      />

      {/* Path navigation */}
      {(selectedItem || !isLoading) && (
        <Stack spacing={1.5}>
          <TreeChip
            label={allItemsLabel || t('labelAll')}
            onClick={() => handleItemChange(null)}
            isMainSelected={!selectedItem}
            isSelected={!!selectedItem}
          />

          {selectedItem?.path &&
            selectedItem.path
              .filter((pathItem: ITreePathItem) => pathItem.level > 0)
              .map((pathItem: ITreePathItem, index: number) => {
                const isMain = pathItem.id === selectedItem.id;

                return renderPathItem(pathItem, isMain, () => {
                  const fullItem = createTreeItemFromPath(pathItem, selectedItem, index);

                  handleItemChange(fullItem);
                });
              })}
        </Stack>
      )}

      {/* Child items */}
      {(childItems.length > 0 || isLoading) && (
        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {isLoading && <Loader />}

          {!isLoading &&
            childItems.map((child: T) => (
              <TreeChip
                key={String(child.id)}
                label={child.name}
                onClick={() => handleItemChange(child)}
              />
            ))}
        </Box>
      )}
    </Stack>
  );
};
